import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/common/ConfirmDialog';
import {
  copyClassRoster,
  createClassRoster,
  deleteClassRoster,
  getRosterDeletePreview,
  listAllClassRosters,
  updateClassRoster,
} from '../../api/classRosterApi';
import { trackClassRosterEvent } from '../../utils/eventTracker';
import {
  ROSTER_MAX_STUDENTS,
  buildNameSheetRows,
  canPrepareNextYear,
  groupRostersByTerm,
  nameSheetFileName,
  parsePastedNames,
  semesterLabel,
  summarizeRosterEdit,
  toStudentPatch,
} from '../../utils/classRosterManage';
import { ROSTER_FILE_ACCEPT, ROSTER_FILE_EXTENSIONS, readRosterFile } from '../../utils/classRosterFile';
import { setPreferredRoster } from '../../utils/preferredRoster';
import ClassRosterEditor from './ClassRosterEditor';
import './ClassesPage.css';

// 예시 문구는 실제 화면(ClassStudentsPage 생기부 쓰기, ClassWriteSection, ClassRosterCard)의 표기를 그대로 따른다.
const VALUE_PREVIEWS = [
  { when: '평소에', text: '누르면 바로 메모, 생기부 근거가 돼요', example: '근거 5개 · 메모 2 · 채점 3' },
  { when: '채점할 때', text: '답안지 이름을 명단과 맞춰요', example: '8명 중 6명을 맞췄어요. 2명만 확인해 주세요' },
  { when: '생기부 쓸 때', text: '모인 메모와 채점으로 반 전체 초안을 한 번에 만들어요', example: '28명 행동특성 쓰기' },
];

const YEAR_TITLES = { current: '올해', next: '새 학년 준비', past: '지난 학년도' };

const START_TARGETS = [
  { key: 'scoring', label: '서술형 채점', to: '/scoring' },
  { key: 'saenggibu', label: '생기부 다중 생성', to: '/create-saenggibu' },
];

const CreateForm = ({ currentSchoolYear, currentSemester, saving, onCreate, onCancel }) => {
  const [label, setLabel] = useState('');
  const [term, setTerm] = useState(`${currentSchoolYear}-${currentSemester}`);
  const [text, setText] = useState('');
  const [fileState, setFileState] = useState({ reading: false, message: '' });
  const [source, setSource] = useState('paste');
  const names = useMemo(() => parsePastedNames(text), [text]);
  const tooMany = names.length > ROSTER_MAX_STUDENTS;

  const loadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setFileState({ reading: true, message: '' });
    const startedAt = Date.now();
    const ext = String(file.name || '').split('.').pop().toLowerCase();
    const logRead = (outcome, namesCount = 0) => trackClassRosterEvent('class_roster_file_read', {
      file_ext: ROSTER_FILE_EXTENSIONS.includes(ext) ? ext : 'other',
      outcome,
      names_count: namesCount,
      elapsed_ms: Date.now() - startedAt,
      size_kb: Math.ceil((file.size || 0) / 1024),
    });
    try {
      const read = await readRosterFile(file);
      logRead(read.length < 2 ? 'no_names' : 'ok', read.length);
      if (read.length < 2) {
        setFileState({ reading: false, message: '이 파일에서 학생 이름을 찾지 못했어요. 이름 열에 "성명"이나 "이름" 머리글이 있는지 확인해 주세요.' });
        return;
      }
      setText(read.join('\n'));
      setSource('file');
      setFileState({ reading: false, message: '' });
    } catch (error) {
      logRead(error?.status === 400 ? 'rejected' : 'error');
      setFileState({
        reading: false,
        message: error?.status === 400 ? error.message : '파일을 읽지 못했어요. 엑셀·CSV·한글·워드·PDF 파일인지 확인해 주세요.',
      });
    }
  };
  const terms = [
    { key: `${currentSchoolYear}-1`, label: '올해 1학기' },
    { key: `${currentSchoolYear}-2`, label: '올해 2학기' },
    ...(canPrepareNextYear() ? [{ key: `${currentSchoolYear + 1}-1`, label: `${currentSchoolYear + 1}학년도` }] : []),
  ];

  const submit = (event) => {
    event.preventDefault();
    if (names.length < 2 || tooMany || saving) return;
    const [schoolYear, semester] = term.split('-').map(Number);
    onCreate({ label, names, semester, schoolYear, createdFrom: source });
  };

  return (
    <form className="classes-create" onSubmit={submit} data-testid="classes-create-form">
      <label className="classes-field">
        <span>명단 이름</span>
        <input
          className="classes-input"
          value={label}
          maxLength={40}
          placeholder="예: 2학년 3반, 3-1 과학"
          onChange={e => setLabel(e.target.value)}
          aria-label="새 명단 이름"
        />
      </label>
      <div className="classes-field">
        <span>언제 쓰나요</span>
        <div className="classes-segment" role="group" aria-label="쓰는 학기">
          {terms.map(item => (
            <button
              key={item.key}
              type="button"
              className={term === item.key ? 'is-active' : ''}
              aria-pressed={term === item.key}
              onClick={() => setTerm(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div className="classes-field">
        <div className="classes-field-head">
          <label htmlFor="classes-create-names">학생 이름</label>
          <label className="classes-file-button">
            <input
              type="file"
              accept={ROSTER_FILE_ACCEPT}
              onChange={loadFile}
              disabled={fileState.reading}
              aria-label="명렬표 파일에서 불러오기"
            />
            {fileState.reading ? '읽는 중…' : '파일에서 불러오기'}
          </label>
        </div>
        <textarea
          id="classes-create-names"
          className="classes-input classes-textarea"
          value={text}
          rows={6}
          placeholder={'명렬표 파일(엑셀·한글·워드·PDF)을 올리거나 이름을 붙여넣으세요\n번호가 함께 있어도 이름만 골라요'}
          onChange={(e) => {
            setText(e.target.value);
            if (!e.target.value.trim()) setSource('paste');
          }}
          aria-label="학생 이름 붙여넣기"
        />
        {fileState.message && <p className="classes-file-error" role="alert">{fileState.message}</p>}
      </div>
      {names.length > 0 && (
        <p className="classes-create-preview" data-testid="classes-create-preview">
          <strong>{names.length}명</strong>을 읽었어요 · {names.slice(0, 6).join(', ')}{names.length > 6 ? ' …' : ''}
        </p>
      )}
      {tooMany && (
        <p className="classes-file-error" role="alert">
          한 명단에는 {ROSTER_MAX_STUDENTS}명까지 저장할 수 있어요. 학년 전체 명렬표라면 반별로 나눠 만들어 주세요.
        </p>
      )}
      <div className="classes-editor-footer">
        <button type="button" className="classes-btn-secondary" onClick={() => onCancel({ namesCount: names.length, source })} disabled={saving}>닫기</button>
        <button type="submit" className="classes-btn-primary" disabled={names.length < 2 || tooMany || saving}>
          {saving ? '저장하는 중…' : '명단 저장하기'}
        </button>
      </div>
    </form>
  );
};

const rosterWhen = (roster, currentSchoolYear) => {
  const year = Number(roster.school_year) || currentSchoolYear;
  return year > currentSchoolYear ? 'next' : year < currentSchoolYear ? 'past' : 'current';
};

const termKey = (year, semester, label) => `${year}-${semester === 2 ? 2 : 1}-${String(label || '').trim()}`;

const RosterCard = ({
  roster, when, currentSchoolYear, currentSemester, takenTerms, open, busy, onToggle, onSave, onCopy, onDelete, onDownload, onStart,
}) => {
  const names = (roster.students || []).map(s => s.name);
  const copyActions = [];
  if (when === 'current' && roster.semester !== 2 && !takenTerms.has(termKey(currentSchoolYear, 2, roster.label))) {
    copyActions.push({ key: 'second', label: '2학기로 이어 쓰기', args: { semester: 2 } });
  }
  if (when === 'past' && !takenTerms.has(termKey(currentSchoolYear, currentSemester, roster.label))) {
    copyActions.push({ key: 'thisYear', label: '올해 명단으로 가져오기', args: {} });
  }
  return (
    <li className={`classes-card${open ? ' is-open' : ''}`} data-testid="classes-roster-card">
      <div className="classes-card-row">
        <Link className="classes-card-main" to={`/classes/${encodeURIComponent(roster.id)}`}>
          <span className="classes-card-title">{roster.label}</span>
          <span className="classes-card-meta">
            {roster.student_count}명 · {names.slice(0, 3).join(', ')}{names.length > 3 ? ' …' : ''}
          </span>
          <span className="classes-card-go">반 열기 ›</span>
        </Link>
        <button
          type="button"
          className="classes-btn-secondary classes-card-edit"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${roster.label} ${open ? '닫기' : '명단 고치기'}`}
        >
          {open ? '닫기' : '명단 고치기'}
        </button>
      </div>
      {open && (
        <div className="classes-card-body">
          {when === 'current' && (
            <div className="classes-card-start" role="group" aria-label="이 명단으로 시작하기">
              <span>이 명단으로 시작하기</span>
              {START_TARGETS.map(target => (
                <button key={target.key} type="button" className="classes-btn-secondary" onClick={() => onStart(roster, target)}>
                  {target.label}
                </button>
              ))}
            </div>
          )}
          <ClassRosterEditor
            key={`${roster.id}-${roster.updated_at || ''}`}
            roster={roster}
            saving={busy === 'save'}
            onSave={patch => onSave(roster, patch)}
            onCancel={onToggle}
          />
          <div className="classes-card-tools">
            <button type="button" className="classes-btn-secondary" onClick={() => onDownload(roster)}>
              명렬표 엑셀 받기
            </button>
            {copyActions.map(action => (
              <button
                key={action.key}
                type="button"
                className="classes-btn-secondary"
                disabled={Boolean(busy)}
                onClick={() => onCopy(roster, action.args)}
              >
                {busy === 'copy' ? '만드는 중…' : action.label}
              </button>
            ))}
            <button
              type="button"
              className="classes-btn-text-danger"
              disabled={Boolean(busy)}
              onClick={() => onDelete(roster)}
              data-testid="classes-delete"
            >
              명단 지우기
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

const ClassesPage = () => {
  const { currentUser } = useAuth();
  const { showToast, removeToast } = useToast();
  const confirmDialog = useConfirm();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', rosters: [], currentSchoolYear: null, currentSemester: 1 });
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState({ id: null, kind: '' });
  const [creating, setCreating] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);

  const viewLoggedRef = useRef(false);
  const load = useCallback(async () => {
    try {
      const data = await listAllClassRosters();
      if (!viewLoggedRef.current) {
        viewLoggedRef.current = true;
        const year = data?.currentSchoolYear || new Date().getFullYear();
        const rosters = data?.rosters || [];
        trackClassRosterEvent('class_roster_page_viewed', {
          roster_count: rosters.length,
          current_count: rosters.filter(r => Number(r.school_year) === year).length,
          next_count: rosters.filter(r => Number(r.school_year) > year).length,
          past_count: rosters.filter(r => Number(r.school_year) < year).length,
        });
      }
      setState({
        loading: false,
        error: '',
        rosters: data?.rosters || [],
        currentSchoolYear: data?.currentSchoolYear || null,
        currentSemester: data?.currentSemester || 1,
      });
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: '명단을 불러오지 못했어요. 잠시 후 다시 시도해주세요.' }));
    }
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    load();
  }, [currentUser, load]);

  const currentSchoolYear = state.currentSchoolYear || new Date().getFullYear();
  const empty = !state.loading && !state.error && state.rosters.length === 0;
  const groups = useMemo(() => groupRostersByTerm(state.rosters, currentSchoolYear), [state.rosters, currentSchoolYear]);
  const takenTerms = useMemo(
    () => new Set(state.rosters.map(r => termKey(r.school_year, r.semester, r.label))),
    [state.rosters],
  );

  const replaceRoster = (roster) => setState(prev => ({
    ...prev,
    rosters: prev.rosters.map(item => (item.id === roster.id ? roster : item)),
  }));

  const openCreate = () => {
    setCreating(true);
    trackClassRosterEvent('class_roster_create_opened', { roster_count: state.rosters.length });
  };

  const cancelCreate = ({ namesCount, source }) => {
    setCreating(false);
    trackClassRosterEvent('class_roster_create_cancelled', { names_count: namesCount, created_from: source });
  };

  const handleCreate = async ({ label, names, semester, schoolYear, createdFrom = 'paste' }) => {
    setCreateSaving(true);
    try {
      const roster = await createClassRoster({ label, names, createdFrom, semester, schoolYear });
      trackClassRosterEvent('class_roster_saved', {
        source: 'classes',
        created_from: createdFrom,
        student_count: roster.student_count,
        semester: roster.semester,
        year_offset: Number(roster.school_year) - currentSchoolYear,
        roster_id: roster.id,
      });
      setCreating(false);
      setOpenId(null);
      await load();
      const toastId = showToast(`${roster.label} 명단을 저장했어요`, 'success', 6000, {
        label: '반 열기',
        onClick: () => {
          removeToast(toastId);
          trackClassRosterEvent('class_roster_records_opened', { surface: 'classes', roster_id: roster.id });
          navigate(`/classes/${encodeURIComponent(roster.id)}`);
        },
      });
    } catch (error) {
      showToast(error.message || '명단을 저장하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSave = async (roster, { label, semester, students }) => {
    setBusy({ id: roster.id, kind: 'save' });
    try {
      const patch = { label, semester, students: toStudentPatch(students, roster) };
      const updated = await updateClassRoster(roster.id, patch);
      replaceRoster(updated);
      if (updated.semester !== roster.semester) await load();
      trackClassRosterEvent('class_roster_edited', {
        ...summarizeRosterEdit(roster, patch),
        student_count: updated.student_count,
        roster_id: roster.id,
      });
      showToast('명단을 저장했어요', 'success');
    } catch (error) {
      showToast(error.message || '명단을 저장하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setBusy({ id: null, kind: '' });
    }
  };

  const handleCopy = async (roster, args) => {
    setBusy({ id: roster.id, kind: 'copy' });
    try {
      const copied = await copyClassRoster(roster.id, args);
      trackClassRosterEvent('class_roster_copied', {
        to_semester: copied.semester,
        year_offset: Number(copied.school_year) - Number(roster.school_year),
        student_count: copied.student_count,
        roster_id: copied.id,
      });
      await load();
      setOpenId(copied.id);
      showToast(`${copied.school_year}학년도 ${semesterLabel(copied.semester)} 명단을 만들었어요`, 'success');
    } catch (error) {
      showToast(error.message || '명단을 복사하지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setBusy({ id: null, kind: '' });
    }
  };

  const handleDelete = async (roster) => {
    setBusy({ id: roster.id, kind: 'delete' });
    let memoCount = null;
    try {
      memoCount = await getRosterDeletePreview(roster.id);
    } catch (error) {
      memoCount = null;
    }
    const memoLine = memoCount === null
      ? '이 명단에만 있는 학생의 메모는 함께 지워져요.'
      : memoCount > 0 ? `학생 메모 ${memoCount}개도 함께 지워져요. 되돌릴 수 없어요.` : '';
    const ok = await confirmDialog({
      title: `${roster.label} 명단을 지울까요?`,
      description: [memoLine, '채점·생기부 기록은 그대로 남아요.', '다음에 올리는 이름은 이 명단으로 맞추지 않아요.']
        .filter(Boolean).join('\n'),
      confirmLabel: '지우기',
      danger: true,
    });
    if (!ok) {
      setBusy({ id: null, kind: '' });
      return;
    }
    try {
      await deleteClassRoster(roster.id);
      const createdAt = Date.parse(roster.created_at || '');
      trackClassRosterEvent('class_roster_deleted', {
        student_count: roster.student_count,
        when: rosterWhen(roster, currentSchoolYear),
        ...(Number.isFinite(createdAt) ? { age_days: Math.floor((Date.now() - createdAt) / 86400000) } : {}),
        roster_id: roster.id,
      });
      setState(prev => ({ ...prev, rosters: prev.rosters.filter(item => item.id !== roster.id) }));
      setOpenId(null);
      showToast('명단을 지웠어요', 'success');
    } catch (error) {
      showToast(error.message || '명단을 지우지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setBusy({ id: null, kind: '' });
    }
  };

  const handleStart = (roster, target) => {
    setPreferredRoster({ id: roster.id, label: roster.label });
    trackClassRosterEvent('class_roster_start', {
      target: target.key,
      student_count: roster.student_count,
      semester: roster.semester,
      roster_id: roster.id,
    });
    navigate(target.to, { state: { startBatch: true } });
  };

  const handleDownload = (roster) => {
    const sheet = XLSX.utils.aoa_to_sheet(buildNameSheetRows(roster));
    sheet['!cols'] = [{ wch: 6 }, { wch: 12 }, { wch: 80 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, '학생명렬표');
    XLSX.writeFile(book, nameSheetFileName(roster));
    trackClassRosterEvent('class_roster_sheet_downloaded', { student_count: roster.student_count, roster_id: roster.id });
    showToast('명렬표를 받았어요. 기록 칸을 채워 생기부 다중 생성에 올리세요', 'success', 6000);
  };

  return (
    <div className="classes-page">
      <Helmet>
        <title>학급 관리 | 왓퀴즈</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <header className="classes-header">
        <div>
          <h1>학급 관리</h1>
          <p>명단을 만들어 두면 채점·과제·생기부 기록이 학생마다 모여요</p>
        </div>
        {!creating && !empty && !state.loading && (
          <button type="button" className="classes-btn-secondary" onClick={openCreate} data-testid="classes-new">
            새 명단 만들기
          </button>
        )}
      </header>

      {creating && (
        <CreateForm
          currentSchoolYear={currentSchoolYear}
          currentSemester={state.currentSemester || 1}
          saving={createSaving}
          onCreate={handleCreate}
          onCancel={cancelCreate}
        />
      )}

      {state.loading && (
        <div className="classes-skeleton" aria-label="명단을 불러오는 중">
          <div /><div />
        </div>
      )}

      {state.error && (
        <div className="classes-error" role="alert">
          <p>{state.error}</p>
          <button type="button" className="classes-btn-secondary" onClick={() => { setState(prev => ({ ...prev, loading: true, error: '' })); load(); }}>
            다시 불러오기
          </button>
        </div>
      )}

      {empty && !creating && (
        <>
          <div className="classes-empty" data-testid="classes-empty">
            <strong>우리 반 명단, 한 번만 만들어 두세요</strong>
            <p>명렬표 파일을 올리거나 이름을 붙여 넣으면 돼요</p>
            <button type="button" className="classes-btn-primary" onClick={openCreate}>명단 만들기</button>
          </div>
          <section className="classes-value" data-testid="classes-value" aria-labelledby="classes-value-title">
            <h2 id="classes-value-title">명단이 있으면 이렇게 돼요</h2>
            <ul>
              {VALUE_PREVIEWS.map(item => (
                <li key={item.when}>
                  <span className="classes-value-when">{item.when}</span>
                  <div className="classes-value-body">
                    <strong>{item.text}</strong>
                    <p className="classes-value-example">
                      <span className="classes-value-tag">예시</span>
                      <span>{item.example}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      {groups.map(group => (
        <section key={group.year} className="classes-year" data-testid={`classes-year-${group.when}`}>
          <h2>
            {group.year}학년도
            <span className={`classes-year-badge is-${group.when}`}>{YEAR_TITLES[group.when]}</span>
          </h2>
          {group.when === 'past' && (
            <p className="classes-year-note">지난 학년도 명단은 이름 맞추기에 쓰지 않아요. 올해도 같은 반이면 올해 명단으로 가져오세요.</p>
          )}
          {group.when === 'next' && (
            <p className="classes-year-note">3월 새 학년이 시작되면 이 명단으로 이름을 맞춰요.</p>
          )}
          {group.terms.map(term => (
            <div key={term.semester} className="classes-term">
              <h3>{semesterLabel(term.semester)}</h3>
              <ul className="classes-card-list">
                {term.rosters.map(roster => (
                  <RosterCard
                    key={roster.id}
                    roster={roster}
                    when={group.when}
                    currentSchoolYear={currentSchoolYear}
                    currentSemester={state.currentSemester || 1}
                    takenTerms={takenTerms}
                    open={openId === roster.id}
                    busy={busy.id === roster.id ? busy.kind : ''}
                    onToggle={() => setOpenId(prev => (prev === roster.id ? null : roster.id))}
                    onSave={handleSave}
                    onCopy={handleCopy}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                    onStart={handleStart}
                  />
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
};

export default ClassesPage;
